import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { requestUser } from 'ducks/admin/users'
import { getUserApps, requestUserApps } from 'ducks/admin/apps'
import { adaloBackendAxios } from '../../utils/io/http/axios'
import Page from '../Shared/Page'
import Button from '../Shared/Button'

import './Admin.scss'

const AdminAppDetail = ({ match }) => {
  const dispatch = useDispatch()
  const [blocking, setBlocking] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [blockMessage, setBlockMessage] = useState(null)
  const [datasources, setDatasources] = useState([])
  const [error, setError] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteMessage, setDeleteMessage] = useState(null)
  const userId = match.params.userId
  const appId = match.params.appId

  const app = useSelector(state =>
    getUserApps(state, userId).find(app => app.id === appId)
  )

  useEffect(() => {
    dispatch(requestUser(userId))
    dispatch(requestUserApps(userId))
    Promise.all([adaloBackendAxios.get(`/apps/${appId}/datasources`)])
      .then(([result]) => {
        const dbs = Object.keys(result.data)
        setDatasources(dbs)
      })
      .catch(err => {
        setError(err)
      })
  }, [dispatch, userId, appId])

  useEffect(() => {
    if (app) {
      setBlocked(app.blocked || false)
    }
  }, [app])

  const handleBlockApp = async () => {
    const reason = window.prompt(
      'Enter a reason for blocking this app (optional):'
    )

    if (
      !window.confirm(
        `Are you sure you want to block the app "${app.name}"?\n\nThis will:\n- Unpublish the app\n- Block all app actions\n- Block database access\n- Block requests to the backend\n- Email the user with the reason (if they have a paid plan)`
      )
    ) {
      return
    }

    setBlocking(true)
    setBlockMessage(null)

    try {
      await adaloBackendAxios.post(`/admin/apps/${app.id}/block`, {
        reason: reason || undefined,
      })

      setBlocked(true)
      setBlockMessage('App successfully blocked!')
      setTimeout(() => setBlockMessage(null), 3000)
    } catch (err) {
      console.error(`Could not block app: ${err.message}`)
      setBlockMessage(`Error: ${err.response?.data?.error || err.message}`)
    } finally {
      setBlocking(false)
    }
  }

  const handleUnblockApp = async () => {
    if (
      !window.confirm(
        `Are you sure you want to unblock the app "${app.name}"?\n\nThis will restore normal access to the app.`
      )
    ) {
      return
    }

    setBlocking(true)
    setBlockMessage(null)

    try {
      await adaloBackendAxios.post(`/admin/apps/${app.id}/unblock`)

      setBlocked(false)
      setBlockMessage('App successfully unblocked!')
      setTimeout(() => setBlockMessage(null), 3000)
    } catch (err) {
      console.error(`Could not unblock app: ${err.message}`)
      setBlockMessage(`Error: ${err.response?.data?.error || err.message}`)
    } finally {
      setBlocking(false)
    }
  }

  const handleDeleteAndBlockOwner = async () => {
    const reason = window.prompt(
      'Enter a reason for deleting this app and blocking the owner (required):'
    )

    if (!reason || reason.trim() === '') {
      alert('A reason is required to delete the app and block the owner.')

      return
    }

    if (
      !window.confirm(
        `⚠️ DANGER: Are you sure you want to DELETE the app "${app.name}" and BLOCK its owner?\n\nThis action will:\n- Permanently delete the app\n- Block the app owner's account\n- Cannot be undone\n\nType the app name to confirm.`
      )
    ) {
      return
    }

    const confirmName = window.prompt(
      `Type the app name "${app.name}" to confirm deletion:`
    )

    if (confirmName !== app.name) {
      alert('App name does not match. Action cancelled.')

      return
    }

    setDeleting(true)
    setDeleteMessage(null)

    try {
      await adaloBackendAxios.delete(
        `/admin/apps/${appId}/delete-and-block-owner`,
        {
          data: { reason },
        }
      )

      setDeleteMessage('App deleted and owner blocked successfully!')
      setTimeout(() => {
        window.location.href = `/admin/users/${userId}`
      }, 2000)
    } catch (err) {
      console.error(`Could not delete app and block owner: ${err.message}`)
      setDeleteMessage(`Error: ${err.response?.data?.error || err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  if (error) {
    return (
      <Page className="admin-app-detail-page" title="App Details">
        <Link to={`/admin/users/${userId}`} className="back-link">
          ← Back to User
        </Link>
        <div style={{ color: '#c62828', marginTop: '20px' }}>
          Error loading datasources
        </div>
      </Page>
    )
  }

  if (!app) {
    return (
      <Page className="admin-app-detail-page" title="App Details">
        <Link to={`/admin/users/${userId}`} className="back-link">
          ← Back to User
        </Link>
        <div style={{ marginTop: '20px' }}>App not found</div>
      </Page>
    )
  }

  return (
    <Page className="admin-app-detail-page" title="App Details">
      <Link to={`/admin/users/${userId}`} className="back-link">
        ← Back to User
      </Link>

      <div className="app-header">
        <h2>
          <Link to={`/apps/${app.id}`}>{app.name}</Link>
          {blocked && <span className="blocked-badge">Blocked</span>}
        </h2>
      </div>

      <div className="app-info">
        <div className="info-row">
          <span className="info-label">App ID:</span>
          <span className="info-value">{app.id}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Created Date:</span>
          <span className="info-value">
            {app.createdAt ? new Date(app.createdAt).toLocaleString() : 'N/A'}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Updated Date:</span>
          <span className="info-value">
            {app.updatedAt ? new Date(app.updatedAt).toLocaleString() : 'N/A'}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Datasource ID:</span>
          <div className="info-value">
            {datasources.length > 0 ? (
              <div className="datasource-list">
                {datasources.map(id => (
                  <Link key={id} to={`/admin/datasource/${id}`}>
                    {id}
                  </Link>
                ))}
              </div>
            ) : (
              <span>None</span>
            )}
          </div>
        </div>
      </div>

      <div className="actions-section">
        {blocked ? (
          <Button
            onClick={handleUnblockApp}
            disabled={blocking}
            large
            outlined
            teal
          >
            {blocking ? 'Unblocking...' : 'Unblock App'}
          </Button>
        ) : (
          <Button onClick={handleBlockApp} disabled={blocking} red large>
            {blocking ? 'Blocking...' : 'Block App'}
          </Button>
        )}
        {blockMessage && (
          <span
            className="action-message"
            style={{ color: blocked ? '#2e7d32' : '#c62828' }}
          >
            {blockMessage}
          </span>
        )}
      </div>

      <div className="danger-zone">
        <h3>Danger Zone</h3>
        <p className="danger-description">
          This action will permanently delete the app and block the owner's
          account. This cannot be undone.
        </p>
        <Button
          onClick={handleDeleteAndBlockOwner}
          disabled={deleting}
          red
          large
        >
          {deleting ? 'Deleting...' : 'Delete All User Apps & Block Owner'}
        </Button>
        {deleteMessage && (
          <div
            className={`delete-message ${
              deleteMessage.includes('Error') ? 'error' : 'success'
            }`}
          >
            {deleteMessage}
          </div>
        )}
      </div>
    </Page>
  )
}

export default AdminAppDetail
